import { useCallback, useEffect, useRef } from 'react';

import { di } from 'react-magnetic-di';

import { EMPTY_CONVERSATION_ID } from '@atlassian/conversation-assistant-store/controllers/chat';
import { useUIStoreActions } from '@atlassian/conversation-assistant-store/controllers/ui-store';
import {
	useConversationActionsStore,
	useConversationActionsStoreActions,
} from '@atlassian/rovo-conversation-actions/conversation-actions-store';
import { type ConversationAction } from '@atlassian/rovo-conversation-actions/types';

import { DEFAULT_AGENT } from '../agents/constants';
import { useChatStoreActions } from '../chat-store/useChatStoreActions';

import { getActionOnDismiss } from './getActionOnDismiss';

const dismissActiveAction = (
	action: ConversationAction | undefined,
	clearAction: () => void,
): void => {
	try {
		getActionOnDismiss(action)?.();
	} finally {
		clearAction();
	}
};

export const useActiveAction = (): {
	activeAction: ConversationAction | undefined;
	setActiveAction: (
		action: ConversationAction | undefined,
		options?: { preserveSelectedConversation?: boolean },
	) => void;
	clearActiveAction: () => void;
} => {
	di(
		useChatStoreActions,
		useConversationActionsStoreActions,
		useConversationActionsStore,
		useUIStoreActions,
	);
	const { setActiveAction_UNSAFE } = useConversationActionsStoreActions();
	const { setSelectedConversation } = useChatStoreActions();
	const { setActiveMenu } = useUIStoreActions();
	const [{ activeAction }] = useConversationActionsStore();
	const activeActionRef = useRef(activeAction);

	// Keep clearActiveAction safe for hook instances that did not set the action.
	useEffect(() => {
		activeActionRef.current = activeAction;
	}, [activeAction]);

	const setActiveAction = useCallback(
		(
			action: ConversationAction | undefined,
			options?: { preserveSelectedConversation?: boolean },
		): void => {
			activeActionRef.current = action;
			setActiveAction_UNSAFE(action);
			if (action) {
				if (!options?.preserveSelectedConversation) {
					setSelectedConversation({
						conversationId: EMPTY_CONVERSATION_ID,
						sourceAgentId: DEFAULT_AGENT.id,
					});
				}
				setActiveMenu('none');
			}
		},
		[setActiveAction_UNSAFE, setSelectedConversation, setActiveMenu],
	);

	const clearActiveAction = useCallback((): void => {
		dismissActiveAction(activeActionRef.current, () => {
			activeActionRef.current = undefined;
			setActiveAction_UNSAFE(undefined);
		});
	}, [setActiveAction_UNSAFE]);

	return {
		activeAction,
		setActiveAction,
		clearActiveAction,
	};
};
